import React, { useEffect } from 'react'

import { connect } from 'containers/store'

import { Description, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { RadioInputElement } from 'components/ui/radio'
import { StatusGuard } from 'components/ui/status-guard'

import { actionCreators, LinkableActionCreators } from 'domains/linkable/actions'
import { LinkableStore } from 'domains/linkable/store'

import { pick } from 'helpers/core'
import { statusSelector } from 'helpers/status'
import { space } from 'helpers/style'

import styled from 'styled'

import { Steps } from '.'

const OptionContainer = styled.div`
  padding: ${space(4)};
`

type Option = {
  readonly id: keyof Steps;
  readonly title: string;
  readonly subtitle: string;
}

type ConnectedProps = {
  entityMappings: LinkableStore['linkable']['entityMappings'];
};

type ActionProps = Pick<LinkableActionCreators, 'linkableGetLinkedEntityMappingsRequest' | 'linkableGetLinkedEntityMappingsReset'>

type DirectProps = {
  readonly selectedType: keyof Steps | undefined;
  readonly setSelectedType: React.Dispatch<keyof Steps>;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const SelectStopTypeView = (props: Props) => {

  const {
    entityMappings,
    linkableGetLinkedEntityMappingsRequest,
    linkableGetLinkedEntityMappingsReset,
    selectedType,
    setSelectedType,
  } = props

  useEffect(() => {
    const sourceTypename = 'mos.org.getty.audio.Stop'
    const targetTypesLoaded = statusSelector.hasData(entityMappings)
    const targetTypesData = targetTypesLoaded && statusSelector.data(entityMappings)!.mappings

    if (targetTypesData && targetTypesData.length && targetTypesData[0].sourceTypename !== sourceTypename) {
      linkableGetLinkedEntityMappingsReset()
    }

    if (!targetTypesLoaded) {
      linkableGetLinkedEntityMappingsRequest(sourceTypename)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const options: ReadonlyArray<Option> = [
    {
      id: 'createStopFromScratch' as const,
      title: 'New stop',
      subtitle: 'Create a new stop from scratch',
    },
    ...(statusSelector.hasData(entityMappings) ?
    statusSelector.data(entityMappings)!.mappings.map(mapping => ({
      id: mapping.targetTypename as keyof Steps,
      title: mapping.createNewMessageTitle,
      subtitle: mapping.createNewMessageSubtitle,
    })) : []),
  ]

  return (
    <StatusGuard status={entityMappings}>
      <OptionContainer>
        {options.map(option => (
          <ListItemLabel key={option.id}>
            <RadioInputElement
              checked={selectedType === option.id}
              onChange={e => setSelectedType(e.target.name as keyof Steps)}
              name={option.id}
                />
            <Description>
              <Title>{option.title}</Title>
              <Subtitle>{option.subtitle}</Subtitle>
            </Description>
          </ListItemLabel>
        ))}
      </OptionContainer>
    </StatusGuard>
  )
}

export const SelectStopType = connect<ConnectedProps, ActionProps, DirectProps>(
  store => ({ entityMappings: store.linkable.entityMappings }),
  pick(
    actionCreators,
    'linkableGetLinkedEntityMappingsRequest',
    'linkableGetLinkedEntityMappingsReset'
  ),
)(SelectStopTypeView);
